import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { supabase } from '../supabaseClient';
import { removeTopicFromCurrentBasket } from './basketSlice';

// Async thunk to fetch workspace details
const fetchWorkspaceDetails = createAsyncThunk(
  'workspaces/fetchWorkspaceDetails',
  async (workspaceId, { getState, rejectWithValue }) => {
    const state = getState().workspaces;
    // Return cached data if already loaded
    if (state.isWorkspaceLoaded) {
      return state.currentWorkspace;
    }

    try {
      // Fetch data only if not already loaded
      const { data: baskets, error: basketsError } = await supabase
        .from('baskets')
        .select('id, name, description, updated_at')
        .eq('workspace_id', workspaceId)
        .order('updated_at', { ascending: false });

      if (basketsError) throw basketsError;

      // Then, fetch all topics with their related data
      const { data: topics, error: topicsError } = await supabase
        .from('topics')
        .select(`
          id,
          name,
          basket_id,
          tag_id,
          created_at,
          updated_at,
          order_index,
          workspace_id,
          user_id,
          relation_description,
          is_master,
          basket:basket_id (
            id,
            name,
            description,
            workspace_id,
            created_at,
            updated_at,
            order_index
          ),
          concepts (
            id,
            text,
            is_highlighted,
            order_index,
            created_at,
            updated_at
          ),
          tags:tag_id (
            id,
            name,
            color,
            workspace_id,
            created_at
          )
        `)
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (topicsError) throw topicsError;

      // Process the topics data
      const processedTopics = topics.map(topic => ({
        ...topic,
        tag: topic.tags,
        concepts: topic.concepts.map(concept => ({
          ...concept,
          is_highlighted: concept.is_highlighted
        }))
      }));


      // Group topics by basket
      const basketsMap = new Map(baskets.map(basket => [basket.id, { ...basket, topics: [] }]));
      processedTopics.forEach(topic => {
        if (topic.basket_id && basketsMap.has(topic.basket_id)) {
          basketsMap.get(topic.basket_id).topics.push(topic);
        }
      });

      const formattedBaskets = Array.from(basketsMap.values());

   
      // Fetch tags
      const { data: tags, error: tagsError } = await supabase
        .from('tags')
        .select('id, name, color')
        .eq('workspace_id', workspaceId);

      if (tagsError) throw tagsError;

      return {
        baskets: formattedBaskets,
        topics: processedTopics,
        tags: tags,
        lastFetchedAt: Date.now(),
        isFetchedFromDatabase: true
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to delete a topic
const deleteTopic = createAsyncThunk(
  'workspaces/deleteTopic',
  async ({ workspaceId, topicId }, { rejectWithValue }) => {
    try {
      const { error } = await supabase
        .from('topics')
        .delete()
        .eq('id', topicId)
        .eq('workspace_id', workspaceId);

      if (error) throw error;
      return topicId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to delete a basket
const deleteBasket = createAsyncThunk(
  'workspaces/deleteBasket',
  async ({ workspaceId, basketId }, { rejectWithValue }) => {
    try {
      const { error } = await supabase
        .from('baskets')
        .delete()
        .eq('id', basketId)
        .eq('workspace_id', workspaceId);

      if (error) throw error;
      return basketId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to add a new topic
const addTopic = createAsyncThunk(
  'workspaces/addTopic',
  async (newTopicData, { rejectWithValue, getState }) => {
    console.log('Starting addTopic with:', newTopicData);
    try {
      const { workspaceId, newTopic } = newTopicData;
      let tagId = newTopic.tag_id; // Use existing tag_id if provided

      // Only handle tag creation if it's a new tag
      if (newTopic.tag && !tagId) {
        console.log('Processing new tag:', newTopic.tag);
        // First check if the tag already exists in the state
        const state = getState();
        const existingTag = state.workspaces.currentWorkspace.tags.find(
          tag => tag.name.toLowerCase() === newTopic.tag.name.toLowerCase() && 
                tag.workspace_id === workspaceId
        );

        if (existingTag) {
          console.log('Using existing tag from state:', existingTag);
          tagId = existingTag.id;
        } else {
          // Create new tag
          console.log('Creating new tag...');
          const newTag = {
            name: newTopic.tag.name,
            color: newTopic.tag.color,
            created_by: newTopic.user_id,
            workspace_id: workspaceId
          };
          console.log('New tag data:', newTag);

          const { data: createdTag, error: createError } = await supabase
            .from('tags')
            .insert([newTag])
            .select()
            .single();

          if (createError) {
            console.error('Error creating tag:', createError);
            throw createError;
          }
          console.log('Successfully created tag:', createdTag);
          tagId = createdTag.id;
        }
      }

      // Insert the new topic with correct tag_id
      const topicToInsert = {
        name: newTopic.name,
        workspace_id: workspaceId,
        basket_id: newTopic.basket_id,
        tag_id: tagId,
        user_id: newTopic.user_id,
        order_index: newTopic.order_index || 0
      };

      console.log('Inserting topic:', topicToInsert);

      const { data: insertedTopic, error: topicError } = await supabase
        .from('topics')
        .insert([topicToInsert])
        .select()
        .single();

      if (topicError) {
        console.error('Error inserting topic:', topicError);
        throw topicError;
      }
      console.log('Successfully inserted topic:', insertedTopic);

      // Update the basket's updated_at timestamp
      if (newTopic.basket_id) {
        const { error: basketError } = await supabase
          .from('baskets')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', newTopic.basket_id)
          .eq('workspace_id', workspaceId);

        if (basketError) throw basketError;
      }

      // Insert concepts if any
      if (newTopic.concepts && newTopic.concepts.length > 0) {
        console.log('Processing concepts:', newTopic.concepts);
        const conceptsToInsert = newTopic.concepts.map(concept => ({
          text: concept.text || concept.name,
          topic_id: insertedTopic.id,
          is_highlighted: concept.is_highlighted
        }));
        console.log('Concepts to insert:', conceptsToInsert);

        const { data: insertedConcepts, error: conceptsError } = await supabase
          .from('concepts')
          .insert(conceptsToInsert);

        if (conceptsError) {
          console.error('Error inserting concepts:', conceptsError);
          throw conceptsError;
        }
        console.log('Successfully inserted concepts:', insertedConcepts);
      }

      // Fetch the complete topic data including related data
      console.log('Fetching complete topic data for ID:', insertedTopic.id);
      const { data: completeTopic, error: fetchError } = await supabase
        .from('topics')
        .select(`
          *,
          concepts(*),
          tags(*)
        `)
        .eq('id', insertedTopic.id)
        .single();

      if (fetchError) {
        console.error('Error fetching complete topic:', fetchError);
        throw fetchError;
      }
      console.log('Successfully fetched complete topic:', completeTopic);

      const processedTopic = {
        ...completeTopic,
        tag: completeTopic.tags,
        concepts: completeTopic.concepts.map(concept => ({
          ...concept,
          is_highlighted: !!concept.is_highlighted
        }))
      };
      console.log('Returning processed topic:', processedTopic);
      return processedTopic;

    } catch (error) {
      console.error('Error in addTopic:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to change a topic's basket
const changeTopic = createAsyncThunk(
  'workspaces/changeTopic',
  async ({ topicId, newBasketId, workspaceId }, { rejectWithValue }) => {
    try {
      // Update the topic's basket_id
      const { data, error } = await supabase
        .from('topics')
        .update({ basket_id: newBasketId })
        .eq('id', topicId)
        .eq('workspace_id', workspaceId)
        .select('*, tags(*), concepts(*)')
        .single();

      if (error) throw error;

      // Update the new basket's updated_at timestamp
      if (newBasketId) {
        const { error: basketError } = await supabase
          .from('baskets')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', newBasketId)
          .eq('workspace_id', workspaceId);

        if (basketError) throw basketError;
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to remove a topic from a basket
const removeTopicFromBasket = createAsyncThunk(
  'workspaces/removeTopicFromBasket',
  async ({ topicId, basketId, workspaceId }, { getState, rejectWithValue }) => {
    try {
      // Just update basket_id, no need to fetch all data
      const { error } = await supabase
        .from('topics')
        .update({ basket_id: null })
        .eq('id', topicId)
        .eq('workspace_id', workspaceId);

      if (error) throw error;

      // Get the existing topic data from state
      const state = getState();
      const existingTopic = state.workspaces.currentWorkspace.topics.find(t => t.id === topicId);
      
      // Return the existing topic data with updated basket_id
      // Make sure to preserve both concepts and tags
      const updatedTopic = {
        ...existingTopic,
        basket_id: null,
        concepts: existingTopic.concepts || [],
        tags: existingTopic.tags || existingTopic.tag // Preserve tags data
      };

      return { topicId, basketId, updatedTopic };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch a basket by its ID
const fetchBasketById = createAsyncThunk(
  'workspaces/fetchBasketById',
  async (basketId, { getState }) => {
    const state = getState();
    const basket = state.workspaces.currentWorkspace.baskets.find(b => b.id === basketId);
    if (!basket) {
      throw new Error('Basket not found');
    }
    return basket;
  }
);

// Async thunk to rename a basket
const renameBasket = createAsyncThunk(
  'workspaces/renameBasket',
  async ({ basketId, newName, workspaceId }, { rejectWithValue }) => {
    try {
      console.log('Renaming basket:', { basketId, newName, workspaceId });
      
      // First, check if the basket exists
      const { data: existingBaskets, error: checkError } = await supabase
        .from('baskets')
        .select('*')
        .eq('id', basketId)
        .eq('workspace_id', workspaceId);

      if (checkError) {
        console.error('Error checking basket:', checkError);
        throw new Error(`Error checking basket: ${checkError.message}`);
      }

      if (!existingBaskets || existingBaskets.length === 0) {
        console.error('No basket found:', { basketId, workspaceId });
        throw new Error(`No basket found with ID ${basketId} in workspace ${workspaceId}`);
      }

      // If the basket exists, proceed with the update
      const { data, error } = await supabase
        .from('baskets')
        .update({ name: newName })
        .eq('id', basketId)
        .eq('workspace_id', workspaceId)
        .select();

      if (error) throw error;
      
      if (!data || data.length === 0) {
        throw new Error('No rows were updated');
      }
      
      console.log('Basket renamed successfully:', data[0]);
      return data[0];
    } catch (error) {
      console.error('Error in renameBasket:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to update a basket
const updateBasket = createAsyncThunk(
  'workspaces/updateBasket',
  async ({ basketId, updatedData, workspaceId }, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from('baskets')
        .update({
          ...updatedData,
          updated_at: new Date().toISOString()
        })
        .eq('id', basketId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update the createBasket thunk
export const createBasket = createAsyncThunk(
  'workspaces/addBasket',
  async (basket, { rejectWithValue }) => {
    try {
      console.log('createBasket thunk - Starting with data:', basket);

      if (!basket.workspace_id) {
        console.error('Missing workspace_id in basket data:', basket);
        throw new Error('workspace_id is required');
      }

      const { data, error } = await supabase
        .from('baskets')
        .insert([{
          name: basket.name,
          workspace_id: basket.workspace_id,
          description: basket.description || '',
          order_index: basket.order_index || 0
        }])
        .select('*')
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('createBasket thunk - Successfully created basket:', data);

      // Return the created basket with an empty topics array
      return {
        ...data,
        topics: []
      };
    } catch (error) {
      console.error('Error in createBasket:', error);
      return rejectWithValue(error.message || 'Failed to create basket');
    }
  }
);

// Update the moveTopic thunk
const moveTopic = createAsyncThunk(
  'workspaces/moveTopic',
  async ({ topicId, fromBasketId, toBasketId, workspaceId, relationDescription }, { rejectWithValue, getState }) => {
    try {
      if (!toBasketId) {
        throw new Error('Target basket ID is required');
      }
      
      const state = getState();
      const existingTopic = state.workspaces.currentWorkspace.topics.find(t => t.id === topicId);

      const { data, error } = await supabase
        .from('topics')
        .update({ 
          basket_id: toBasketId,
          relation_description: relationDescription || null
        })
        .eq('id', topicId)
        .eq('workspace_id', workspaceId)
        .select(`
          *,
          concepts (*),
          tags:tag_id (*)
        `)
        .single();

      if (error) throw error;

      // Simplified topic processing
      const processedTopic = {
        ...existingTopic,
        ...data,
        basket_id: toBasketId,
        relation_description: relationDescription || null,
        concepts: existingTopic.concepts || [],
        tag: existingTopic.tag || existingTopic.tags,
        tags: existingTopic.tags || existingTopic.tag
      };

      // Update timestamps
      if (fromBasketId) {
        await supabase
          .from('baskets')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', fromBasketId)
          .eq('workspace_id', workspaceId);
      }

      if (toBasketId) {
        await supabase
          .from('baskets')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', toBasketId)
          .eq('workspace_id', workspaceId);
      }

      return { topic: processedTopic };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Find the updateTopic action creator
export const updateTopic = createAsyncThunk(
  'workspaces/updateTopic',
  async ({ topicId, updates, workspaceId }, { rejectWithValue }) => {
    try {
      // First update the topic
      const { data: updatedTopic, error: updateError } = await supabase
        .from('topics')
        .update(updates)
        .eq('id', topicId)
        .eq('workspace_id', workspaceId)
        .select(`
          *,
          concepts (*),
          tags:tag_id (*)
        `)
        .single();

      if (updateError) throw updateError;

      // Process the topic data to match the expected format
      const processedTopic = {
        ...updatedTopic,
        tag: updatedTopic.tags,
        concepts: updatedTopic.concepts || []
      };

      return { topic: processedTopic };
    } catch (error) {
      console.error('Error in updateTopic:', error);
      return rejectWithValue(error);
    }
  }
);

// Add this with the other async thunks
const updateConceptImportance = createAsyncThunk(
  'workspaces/updateConceptImportance',
  async ({ conceptId, topicId, newHighlightedState }, { rejectWithValue }) => {
    try {
      // First update all concepts to not highlighted
      const { error: updateAllError } = await supabase
        .from('concepts')
        .update({ is_highlighted: false })
        .eq('topic_id', topicId);

      if (updateAllError) throw updateAllError;

      // Then update the selected concept if it's being highlighted
      if (newHighlightedState) {
        const { error: updateSelectedError } = await supabase
          .from('concepts')
          .update({ is_highlighted: true })
          .eq('id', conceptId);

        if (updateSelectedError) throw updateSelectedError;
      }

      return { conceptId, topicId, newHighlightedState };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  currentWorkspace: {
    baskets: [],
    topics: [],
    tags: [], // Initialize tags as an empty array
    status: 'idle',
    error: null,
    lastFetchedAt: null,
    isFetchedFromDatabase: false,
  },
  currentBasket: null,
  basketContentChanged: false,
  isWorkspaceLoaded: false,
};

// Create the workspaces slice
const workspacesSlice = createSlice({
  name: 'workspaces',
  initialState,
  reducers: {
    // Add a new basket to the current workspace
    addBasket: (state, action) => {
      const newBasket = {
        ...action.payload,
        topics: action.payload.topics || []
      };
      state.currentWorkspace.baskets.push(newBasket);
    },
    // Update the last fetched timestamp
    updateLastFetchedAt: (state, action) => {
      state.currentWorkspace.lastFetchedAt = action.payload;
    },
    // Change a topic's basket (this might be redundant with the changeTopic thunk)
    changeTopic: (state, action) => {
      const { topicId, newBasketId } = action.payload;
      const workspace = state.currentWorkspace;
      
      // Find the topic in the current workspace
      const topic = workspace.topics.find(t => t.id === topicId);
      if (topic) {
        // Remove the topic from its current basket
        const oldBasket = workspace.baskets.find(b => b.id === topic.basket_id);
        if (oldBasket) {
          oldBasket.topics = oldBasket.topics.filter(t => t.id !== topicId);
        }
        
        // Update the topic's basket_id
        topic.basket_id = newBasketId;
        
        // Add the topic to the new basket
        const newBasket = workspace.baskets.find(b => b.id === newBasketId);
        if (newBasket) {
          newBasket.topics.push(topic);
        }
      }
    },
    // Set the basket content changed flag
    setBasketContentChanged: (state, action) => {
      state.basketContentChanged = action.payload;
    },
    setCurrentBasket: (state, action) => {
      const basketId = action.payload;
      console.log('Setting current basket with ID:', basketId);
      if (basketId) {
        const basket = state.currentWorkspace.baskets.find(b => b.id === basketId);
        if (basket) {
          console.log('Current basket set to:', basket.name);
          state.currentBasket = basket;
        } else {
          console.warn(`Basket with id ${basketId} not found in current workspace`);
          state.currentBasket = null;
        }
      } else {
        console.log('Clearing current basket');
        state.currentBasket = null;
      }
    },
    // Rename updateBaskets to setBaskets
    setBaskets: (state, action) => {
      state.currentWorkspace.baskets = action.payload;
    },

    // Add a new reducer to update a single basket in the state
    updateBasketInState: (state, action) => {
      const updatedBasket = action.payload;
      const index = state.currentWorkspace.baskets.findIndex(b => b.id === updatedBasket.id);
      if (index !== -1) {
        state.currentWorkspace.baskets[index] = {
          ...state.currentWorkspace.baskets[index],
          ...updatedBasket
        };
      }
      if (state.currentBasket && state.currentBasket.id === updatedBasket.id) {
        state.currentBasket = {
          ...state.currentBasket,
          ...updatedBasket
        };
      }
    },
    setWorkspaceLoaded: (state, action) => {
      state.isWorkspaceLoaded = action.payload;
    },
    resetWorkspaceState: (state) => {
      state.isWorkspaceLoaded = false;
      state.currentWorkspace = initialState.currentWorkspace;
      state.currentBasket = null;
      state.basketContentChanged = false;
    },
    addBasketToWorkspace: (state, action) => {
      const { workspaceId, basket } = action.payload;
      
      if (!basket.id) {
        console.error('addBasketToWorkspace - Basket is missing ID:', basket);
        return; // Early return to prevent errors
      }
      
      const existingBasketIndex = state.currentWorkspace.baskets.findIndex(b => b.id === basket.id);
      
      if (existingBasketIndex !== -1) {
        // Update existing basket
        state.currentWorkspace.baskets[existingBasketIndex] = {
          ...state.currentWorkspace.baskets[existingBasketIndex],
          ...basket,
          topics: [...(basket.topics || [])]
        };
      } else {
        // Add new basket
        state.currentWorkspace.baskets.push({
          ...basket,
          topics: basket.topics || []
        });
      }
    },
    moveBasketToTop: (state, action) => {
      const { basketId } = action.payload;
      
      if (!basketId) {
        return; // Early return to prevent errors
      }
      
      const { baskets } = state.currentWorkspace;
      
      // Find the basket to move
      const basketIndex = baskets.findIndex(b => b.id === basketId);
      
      if (basketIndex === -1) {
        return; // Early return if basket not found
      }
      
      if (basketIndex > 0) { // Only move if not already at top
        const [basket] = baskets.splice(basketIndex, 1);
        baskets.unshift(basket);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchWorkspaceDetails states
      .addCase(fetchWorkspaceDetails.pending, (state) => {
        state.currentWorkspace.status = 'loading';
      })
      .addCase(fetchWorkspaceDetails.fulfilled, (state, action) => {
        state.currentWorkspace = {
          ...action.payload,
          status: 'succeeded',
          error: null
        };
        state.basketContentChanged = false;
        state.isWorkspaceLoaded = true;
        
        // If there's a current basket, update it
        if (state.currentBasket) {
          const updatedBasket = action.payload.baskets.find(b => b.id === state.currentBasket.id);
          state.currentBasket = updatedBasket || null;
        }
      })
      .addCase(fetchWorkspaceDetails.rejected, (state, action) => {
        state.currentWorkspace.status = 'failed';
        state.currentWorkspace.error = action.error.message;
      })
      // Handle deleteTopic states
      .addCase(deleteTopic.fulfilled, (state, action) => {
        const deletedTopicId = action.payload;
        state.currentWorkspace.topics = state.currentWorkspace.topics.filter(topic => topic.id !== deletedTopicId);
        state.currentWorkspace.baskets = state.currentWorkspace.baskets.map(basket => ({
          ...basket,
          topics: basket.topics.filter(topic => topic.id !== deletedTopicId)
        }));
      })
      .addCase(deleteTopic.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      })
      // Handle deleteBasket states
      .addCase(deleteBasket.fulfilled, (state, action) => {
        const deletedBasketId = action.payload;
        state.currentWorkspace.baskets = state.currentWorkspace.baskets.filter(basket => basket.id !== deletedBasketId);
      })
      .addCase(deleteBasket.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      })
      // Handle addTopic states
      .addCase(addTopic.fulfilled, (state, action) => {
        const updatedTopic = action.payload;
        if (updatedTopic) {
          // Update topics array
          const topicIndex = state.currentWorkspace.topics.findIndex(t => t.id === updatedTopic.id);
          if (topicIndex !== -1) {
            state.currentWorkspace.topics[topicIndex] = updatedTopic;
          } else {
            state.currentWorkspace.topics.push(updatedTopic);
          }

          // Update basket's topics and updated_at
          if (updatedTopic.basket_id) {
            const basketIndex = state.currentWorkspace.baskets.findIndex(b => b.id === updatedTopic.basket_id);
            if (basketIndex !== -1) {
              const basket = state.currentWorkspace.baskets[basketIndex];
              // Remove basket from current position
              state.currentWorkspace.baskets.splice(basketIndex, 1);
              // Update basket
              basket.topics = basket.topics.filter(t => t.id !== updatedTopic.id);
              basket.topics.push(updatedTopic);
              basket.updated_at = new Date().toISOString();
              // Add basket to the beginning of the array
              state.currentWorkspace.baskets.unshift(basket);
            }
          }

          // Add the new tag to the tags array if it doesn't exist
          if (updatedTopic.tag && !state.currentWorkspace.tags.some(t => t.id === updatedTopic.tag.id)) {
            state.currentWorkspace.tags.push(updatedTopic.tag);
          }
        }
      })
      .addCase(addTopic.rejected, (state, action) => {
        console.error('addTopic.rejected:', action.error);
        state.currentWorkspace.error = action.error.message;
      })
      // Handle changeTopic states
      .addCase(changeTopic.fulfilled, (state, action) => {
        const updatedTopic = action.payload;
        
        // Update the topic in the topics array
        const topicIndex = state.currentWorkspace.topics.findIndex(t => t.id === updatedTopic.id);
        if (topicIndex !== -1) {
          state.currentWorkspace.topics[topicIndex] = updatedTopic;
        }

        // Update baskets
        state.currentWorkspace.baskets = state.currentWorkspace.baskets.map(basket => {
          // Remove topic from old basket
          if (basket.topics.some(t => t.id === updatedTopic.id)) {
            return {
              ...basket,
              topics: basket.topics.filter(t => t.id !== updatedTopic.id)
            };
          }
          // Add topic to new basket and update timestamp
          if (basket.id === updatedTopic.basket_id) {
            const updatedBasket = {
              ...basket,
              topics: [...basket.topics, updatedTopic],
              updated_at: new Date().toISOString()
            };
            // Move this basket to the beginning of the array
            const otherBaskets = state.currentWorkspace.baskets.filter(b => b.id !== basket.id);
            state.currentWorkspace.baskets = [updatedBasket, ...otherBaskets];
            return updatedBasket;
          }
          return basket;
        });

        // Update currentBasket if it exists
        if (state.currentBasket && state.currentBasket.id === updatedTopic.basket_id) {
          const existingTopicIndex = state.currentBasket.topics.findIndex(t => t.id === updatedTopic.id);
          if (existingTopicIndex === -1) {
            state.currentBasket.topics.push(updatedTopic);
          } else {
            state.currentBasket.topics[existingTopicIndex] = updatedTopic;
          }
        }
      })
      // Handle removeTopicFromBasket states
      .addCase(removeTopicFromBasket.fulfilled, (state, action) => {
        const { topicId, basketId, updatedTopic } = action.payload;
        
        // Update the topic in the main topics array
        const topicIndex = state.currentWorkspace.topics.findIndex(t => t.id === topicId);
        if (topicIndex !== -1) {
          state.currentWorkspace.topics[topicIndex] = updatedTopic;
        }

        // Remove the topic from the basket
        const basketIndex = state.currentWorkspace.baskets.findIndex(b => b.id === basketId);
        if (basketIndex !== -1) {
          state.currentWorkspace.baskets[basketIndex].topics = 
            state.currentWorkspace.baskets[basketIndex].topics.filter(t => t.id !== topicId);
        }

        // Update currentBasket if it's the affected basket
        if (state.currentBasket && state.currentBasket.id === basketId) {
          state.currentBasket.topics = state.currentBasket.topics.filter(t => t.id !== topicId);
        }
      })
      .addCase(removeTopicFromBasket.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      })
      // Handle fetchBasketById states
      .addCase(fetchBasketById.fulfilled, (state, action) => {
        state.currentBasket = action.payload;
      })
      .addCase(fetchBasketById.rejected, (state, action) => {
        state.error = action.error.message;
      })
      // Handle renameBasket states
      .addCase(renameBasket.fulfilled, (state, action) => {
        const updatedBasket = action.payload;
        const basketIndex = state.currentWorkspace.baskets.findIndex(b => b.id === updatedBasket.id);
        if (basketIndex !== -1) {
          state.currentWorkspace.baskets[basketIndex].name = updatedBasket.name;
        }
        if (state.currentBasket && state.currentBasket.id === updatedBasket.id) {
          state.currentBasket.name = updatedBasket.name;
        }
      })
      .addCase(renameBasket.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      })
      // Handle updateBasket states
      .addCase(updateBasket.fulfilled, (state, action) => {
        workspacesSlice.caseReducers.updateBasketInState(state, action);
      })
      .addCase(updateBasket.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      })
      // Add these cases for moveTopic
      .addCase(moveTopic.fulfilled, (state, action) => {
        const { topic } = action.payload;
        
        // Update the topic in the main topics array
        const topicIndex = state.currentWorkspace.topics.findIndex(t => t.id === topic.id);
        if (topicIndex !== -1) {
          state.currentWorkspace.topics[topicIndex] = {
            ...topic,
            relation_description: topic.relation_description
          };
        }

        // Update baskets
        state.currentWorkspace.baskets = state.currentWorkspace.baskets.map(basket => {
          // Remove from old basket
          if (basket.topics.some(t => t.id === topic.id)) {
            return {
              ...basket,
              topics: basket.topics.filter(t => t.id !== topic.id),
              updated_at: new Date().toISOString()
            };
          }
          // Add to new basket
          if (basket.id === topic.basket_id) {
            return {
              ...basket,
              topics: [...basket.topics, {
                ...topic,
                relation_description: topic.relation_description
              }],
              updated_at: new Date().toISOString()
            };
          }
          return basket;
        });
      })
      .addCase(moveTopic.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      })
      // Add these cases for createBasket
      .addCase(createBasket.fulfilled, (state, action) => {
        const newBasket = action.payload;
        
        // Check if basket already exists to prevent duplication
        const basketExists = state.currentWorkspace.baskets.some(
          basket => basket.id === newBasket.id
        );
        
        if (!basketExists) {
          // Only add if it doesn't exist
          state.currentWorkspace.baskets = [
            newBasket,
            ...state.currentWorkspace.baskets
          ];
        }
        
        state.status = 'succeeded';
      })
      .addCase(createBasket.rejected, (state, action) => {
        console.error('createBasket.rejected:', action.payload);
        state.currentWorkspace.error = action.payload;
      })
      // Add these cases in the extraReducers builder
      .addCase(updateTopic.fulfilled, (state, action) => {
        const { topic: updatedTopic } = action.payload;
        
        // Update in topics array
        const topicIndex = state.currentWorkspace.topics.findIndex(t => t.id === updatedTopic.id);
        if (topicIndex !== -1) {
          state.currentWorkspace.topics[topicIndex] = {
            ...state.currentWorkspace.topics[topicIndex],
            ...updatedTopic
          };
        }

        // Update in baskets
        state.currentWorkspace.baskets = state.currentWorkspace.baskets.map(basket => ({
          ...basket,
          topics: basket.topics.map(topic => 
            topic.id === updatedTopic.id 
              ? { ...topic, ...updatedTopic }
              : topic
          )
        }));

        // Update in currentBasket if it exists
        if (state.currentBasket && state.currentBasket.topics) {
          state.currentBasket.topics = state.currentBasket.topics.map(topic =>
            topic.id === updatedTopic.id 
              ? { ...topic, ...updatedTopic }
              : topic
          );
        }
      })
      .addCase(updateTopic.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      })
      .addCase(updateConceptImportance.fulfilled, (state, action) => {
        const { conceptId, topicId, newHighlightedState } = action.payload;
        
        // Update in topics array
        state.currentWorkspace.topics = state.currentWorkspace.topics.map(topic => {
          if (topic.id === topicId) {
            return {
              ...topic,
              concepts: topic.concepts.map(concept => ({
                ...concept,
                is_highlighted: concept.id === conceptId ? newHighlightedState : false
              }))
            };
          }
          return topic;
        });

        // Update in baskets
        state.currentWorkspace.baskets = state.currentWorkspace.baskets.map(basket => ({
          ...basket,
          topics: basket.topics.map(topic => {
            if (topic.id === topicId) {
              return {
                ...topic,
                concepts: topic.concepts.map(concept => ({
                  ...concept,
                  is_highlighted: concept.id === conceptId ? newHighlightedState : false
                }))
              };
            }
            return topic;
          })
        }));
      })
      .addCase(updateConceptImportance.rejected, (state, action) => {
        state.currentWorkspace.error = action.payload;
      });
  },
});

// Export actions from the slice
const { 
  addBasket, 
  updateLastFetchedAt, 
  setBasketContentChanged, 
  setCurrentBasket, 
  setBaskets, 
  updateBasketInState, 
  setWorkspaceLoaded, 
  resetWorkspaceState, 
  addBasketToWorkspace, 
  moveBasketToTop 
} = workspacesSlice.actions;

// Export the reducer as default
export default workspacesSlice.reducer;

// Export all necessary functions and actions
export {
  fetchWorkspaceDetails,
  deleteTopic,
  deleteBasket,
  addTopic,
  changeTopic,
  updateLastFetchedAt,
  removeTopicFromBasket,
  setBasketContentChanged,
  fetchBasketById,
  setCurrentBasket,
  renameBasket,
  updateBasket,
  setBaskets,
  updateBasketInState,
  setWorkspaceLoaded,
  resetWorkspaceState,
  addBasket,
  addBasketToWorkspace,
  updateConceptImportance,
  moveBasketToTop,
  moveTopic,
};

// Update the selectCurrentWorkspace selector
export const selectCurrentWorkspace = createSelector(
  [(state) => state.workspaces],
  (workspaces) => ({
    ...workspaces.currentWorkspace,
    isWorkspaceLoaded: workspaces.isWorkspaceLoaded
  })
);

// These memoized selectors use createSelector from Redux Toolkit
// They help optimize performance by only recalculating when their inputs change
// selectCurrentWorkspace is a simple selector that returns the current workspace state
// selectTopicsForWorkspace filters topics for a specific workspace
// selectTopicsForBasket filters topics for a specific basket
export const selectTopicsForWorkspace = createSelector(
  [selectCurrentWorkspace, (state, workspaceId) => workspaceId],
  (currentWorkspace, workspaceId) => 
    currentWorkspace.topics.filter(topic => topic.workspace_id === workspaceId)
);

export const selectCurrentBasket = state => state.workspaces.currentBasket;

export const selectTopicsForBasket = createSelector(
  [selectCurrentWorkspace, selectCurrentBasket, (state, basketId) => basketId],
  (currentWorkspace, currentBasket, basketId) => {
    if (!currentWorkspace || !currentWorkspace.baskets) {
      console.log('No workspace or baskets available');
      return [];
    }

    const basket = basketId ? 
      currentWorkspace.baskets.find(b => b.id === basketId) : 
      currentBasket;

    if (!basket) {
      console.log('Basket not found:', basketId);
      return [];
    }

    console.log('Topics for basket:', basket.topics);
    return basket.topics || [];
  }
);

export const selectWorkspaceDetails = (state, workspaceId) => 
  state.workspaces.workspaces[workspaceId];

// Add these memoized selectors at the bottom of workspacesSlice.js
// Base selectors
const selectWorkspacesState = state => state.workspaces;
const selectCurrentWorkspaceState = state => state.workspaces.currentWorkspace;

// Memoized selectors
export const selectAllTopics = createSelector(
  [selectCurrentWorkspaceState],
  currentWorkspace => currentWorkspace.topics || []
);

export const selectAllBaskets = createSelector(
  [selectCurrentWorkspaceState],
  currentWorkspace => currentWorkspace.baskets || []
);

export const selectAllTags = createSelector(
  [selectCurrentWorkspaceState],
  currentWorkspace => currentWorkspace.tags || []
);

// Selector for available topics (not in current basket)
export const selectAvailableTopics = createSelector(
  [selectAllTopics, (state, currentBasketId) => currentBasketId],
  (topics, currentBasketId) => 
    topics.filter(topic => topic.basket_id !== currentBasketId)
);

// Selector for topics in a specific basket
export const selectBasketTopics = createSelector(
  [selectAllTopics, (state, basketId) => basketId],
  (topics, basketId) => 
    topics.filter(topic => topic.basket_id === basketId)
);

